import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { track } from '../analytics';
import { usePerm } from '../hooks/usePerm';
import { SUBSCRIPTION_TAB_ENABLED } from '../branding';

// 'YYYY-MM-DD ...' → DD.MM.YYYY
const fmt = (dt) => {
  if (!dt) return '';
  const [y, m, d] = dt.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
};

const money = (v, cur) => `${Number(v || 0).toLocaleString()} ${cur || ''}`.trim();

// Несгибаемые баннеры лестницы напоминаний (due/overdue/suspended) — не закрываются.
const STYLES = {
  due: { bg: '#3b3413', border: '#a98c1f', color: '#fde68a' },
  overdue: { bg: '#3b250f', border: '#c2641f', color: '#fdba74' },
  suspended: { bg: '#3b1414', border: '#c23a3a', color: '#fca5a5' },
};
// Мягкая плашка (промо кончилось / счёт выставлен) — одна, закрываемая.
const SOFT = { bg: '#12233f', border: '#2f4f86', color: '#bfdbfe' };

// Показывается тем, кто видит подписку (subscription.view).
export default function SubscriptionBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [notice, setNotice] = useState(null);
  const [limitNotice, setLimitNotice] = useState(null);

  // Баннер — только для управляющих ролей (рабочему платёжный нудж не нужен).
  const { can } = usePerm();
  const canSee = can('subscription.view');

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    api.get('/billing/me')
      .then((d) => {
        if (!alive) return;
        setSub(d?.subscription || null);
        setNotice(d?.notice && !d.notice.dismissed ? d.notice : null);
        setLimitNotice(d?.limitNotice || null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [canSee]);

  const state = sub?.state;
  const hard = state && STYLES[state] ? state : null;
  const softKind = !hard && notice ? notice.kind : null;
  // Лимит тарифа (фаза 4) — тише биллинговых событий: показываем, когда их нет.
  const lim = !hard && !softKind && limitNotice ? limitNotice.limit : null;
  const limKey = lim ? limitNotice.key : null;

  useEffect(() => {
    if (hard) track('subscription_banner_shown', { state: hard });
  }, [hard]);
  useEffect(() => {
    if (softKind) track('billing_notice_shown', { kind: softKind });
  }, [softKind]);
  useEffect(() => {
    if (limKey && lim) track('billing_limit_shown', { meter: lim.meter, level: lim.level, policy: lim.policy });
  }, [limKey, lim]);

  const openSubscription = (event, props) => {
    track(event, props);
    navigate('/settings?tab=sub');
  };

  const dismiss = () => {
    if (lim) {
      track('billing_limit_dismissed', { meter: lim.meter, level: lim.level, policy: lim.policy });
      setLimitNotice(null);
      api.post('/billing/notice/dismiss', { key: limKey }).catch(() => {});
      return;
    }
    const key = notice?.key;
    track('billing_notice_dismissed', { kind: softKind });
    setNotice(null); // сразу убираем — сервер догонит
    if (key) api.post('/billing/notice/dismiss', { key }).catch(() => {});
  };

  if (!hard && !softKind && !lim) return null;

  let s;
  let text;
  if (hard) {
    s = STYLES[hard];
    if (hard === 'due') text = t('billing.banner.due', { date: fmt(sub.payBy) });
    else if (hard === 'overdue') text = t('billing.banner.overdue', { days: sub.daysUntilSuspend });
    else text = t('billing.banner.suspended');
  } else if (lim) {
    // Пакет выбран при жёстком лимите — жёлтым, как «скоро оплата»; остальное — мягко.
    s = lim.level === 'full' && lim.policy === 'hard_limit' ? STYLES.due : SOFT;
    text = t(`billing.limit.${lim.level}_${lim.policy}`, {
      current: lim.current, included: lim.included, what: t(`billing.limit.what.${lim.meter}`),
    });
  } else {
    s = SOFT;
    text = softKind === 'invoice'
      ? t('billing.notice.invoice', {
        from: fmt(sub.invoice?.cycleStart), to: fmt(sub.invoice?.cycleEnd),
        amount: money(sub.invoice?.amount, sub.invoice?.currency), payBy: fmt(sub.payBy),
      })
      : t('billing.notice.promoEnded', { date: fmt(sub.periodEnd), invoiceDate: fmt(sub.cycleEnd) });
  }

  return (
    <div
      className="subscription-banner"
      data-ph-capture-attribute-component={hard ? 'subscription_banner' : (lim ? 'billing_limit' : 'billing_notice')}
      style={{
        background: s.bg, border: `1px solid ${s.border}`, color: s.color,
        borderRadius: 12, padding: '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 14, flex: '1 1 240px' }}>{text}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Вкладки «Подписка» на инстансе нет (vsenapole, временно) — перехода
            тоже нет: иначе «Подробнее» молча открывает «Организацию». Текст
            плашки/баннера самодостаточен. */}
        {SUBSCRIPTION_TAB_ENABLED && (
        <button
          data-ph-capture-attribute-action={hard ? 'subscription_banner_cta' : (lim ? 'billing_limit_cta' : 'billing_notice_cta')}
          onClick={() => {
            if (hard) openSubscription('subscription_banner_cta', { state: hard });
            else if (lim) openSubscription('billing_limit_cta', { meter: lim.meter, level: lim.level });
            else openSubscription('billing_notice_cta', { kind: softKind });
          }}
          style={{
            background: 'transparent', border: `1px solid ${s.border}`, color: s.color,
            borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {t('billing.banner.cta')}
        </button>
        )}
        {!hard && (
          <button
            aria-label={t('billing.notice.close')}
            title={t('billing.notice.close')}
            data-ph-capture-attribute-action={lim ? 'billing_limit_dismiss' : 'billing_notice_dismiss'}
            onClick={dismiss}
            style={{
              background: 'transparent', border: 'none', color: s.color,
              fontSize: 18, lineHeight: 1, padding: '4px 6px', cursor: 'pointer', opacity: 0.8,
            }}
          >
            ×
          </button>
        )}
      </span>
    </div>
  );
}
